import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Table, Button, Input, Space, Tag, Modal, Form, Card, Typography, notification, Select } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchOutlined, EditOutlined, EyeOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { PlanLevelEnum } from "../../enums/PlanLevelEnum";
import { IOrganisation } from "../../services/nestjs-base-api/types";
import { useGetAdminOrganisationsQuery } from "../../services/nestjs-base-api";

const { Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;

const AdminOrganisationsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // Fetch organisations data
  const { data, error, isLoading, refetch } = useGetAdminOrganisationsQuery({ 
    jwtToken: user.jwtToken 
  }, { pollingInterval: 600000 });

  useEffect(() => {
    if (error) {
      if ('status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  // Check if data is already an array (direct response) or has a nested data property
  const organisations = Array.isArray(data) ? data : (data?.data || []);
  
  // Function to get plan name based on enum or string value
  const getPlanName = (plan: PlanLevelEnum | string): string => {
    // Handle string values like "PLAN_1" directly from API
    const planValue = typeof plan === 'string' ? plan : String(plan);

    switch (planValue) {
      case PlanLevelEnum.Plan1:
      case "PLAN_1":
        return intl.formatMessage({ id: 'organisation.plan.basic' }) || 'Basic';
      case PlanLevelEnum.Plan2:
      case "PLAN_2":
        return intl.formatMessage({ id: 'organisation.plan.standard' }) || 'Standard';
      case PlanLevelEnum.Plan3:
      case "PLAN_3":
        return intl.formatMessage({ id: 'organisation.plan.professional' }) || 'Professional';
      case PlanLevelEnum.Plan4:
      case "PLAN_4":
        return intl.formatMessage({ id: 'organisation.plan.enterprise' }) || 'Enterprise';
      default:
        return String(plan) || 'Basic';
    }
  };

  // Function to get plan color based on enum or string value
  const getPlanColor = (plan: PlanLevelEnum | string): string => {
    // Handle string values like "PLAN_1" directly from API
    const planValue = typeof plan === 'string' ? plan : String(plan);

    switch (planValue) {
      case PlanLevelEnum.Plan1:
      case "PLAN_1":
        return 'blue';
      case PlanLevelEnum.Plan2:
      case "PLAN_2":
        return 'cyan';
      case PlanLevelEnum.Plan3:
      case "PLAN_3":
        return 'purple';
      case PlanLevelEnum.Plan4:
      case "PLAN_4":
        return 'gold';
      default:
        return 'default';
    }
  };

  // Handle organisation view
  const handleViewOrganisation = (id: number) => {
    navigate(`/admin/organisations/${id}`);
  };

  // Handle organisation edit
  const handleEditOrganisation = (id: number) => {
    navigate(`/admin/organisations/${id}/edit`);
  };

  // Handle organisation delete
  const handleDeleteOrganisation = (id: number, name: string) => {
    Modal.confirm({
      title: intl.formatMessage(
        { id: 'admin.organisations.deleteConfirmTitle' },
        { name }
      ) || `Delete Organisation: ${name}`,
      content: intl.formatMessage(
        { id: 'admin.organisations.deleteConfirmMessage' },
        { name }
      ) || `Are you sure you want to delete ${name}? This action cannot be undone.`,
      okText: intl.formatMessage({ id: 'common.delete' }) || 'Delete',
      cancelText: intl.formatMessage({ id: 'common.cancel' }) || 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => {
        // In a real implementation, this would call the API to delete the organisation
        notification.success({
          message: intl.formatMessage({ id: 'admin.organisations.deleteSuccess' }) || 'Organisation Deleted',
          description: intl.formatMessage(
            { id: 'admin.organisations.deleteSuccessDesc' },
            { name }
          ) || `${name} has been successfully deleted.`,
        });
      },
    });
  };

  // Handle create organisation
  const handleCreateOrganisation = (values: any) => {
    // In a real implementation, this would call the API to create a new organisation
    notification.success({
      message: intl.formatMessage({ id: 'admin.organisations.createSuccess' }) || 'Organisation Created',
      description: intl.formatMessage(
        { id: 'admin.organisations.createSuccessDesc' },
        { name: values.name }
      ) || `${values.name} has been successfully created.`,
    });
    
    setCreateModalVisible(false);
    form.resetFields();
    refetch();
  };

  // Table columns
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IOrganisation, b: IOrganisation) => a.id - b.id,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IOrganisation, b: IOrganisation) => a.name.localeCompare(b.name),
      // Using simpler filtering without onFilter
      filterSearch: true,
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan: PlanLevelEnum | string) => (
        <Tag color={getPlanColor(plan)}>
          {getPlanName(plan)}
        </Tag>
      ),
      filters: [
        { text: 'Basic', value: PlanLevelEnum.Plan1 },
        { text: 'Standard', value: PlanLevelEnum.Plan2 },
        { text: 'Professional', value: PlanLevelEnum.Plan3 },
        { text: 'Enterprise', value: PlanLevelEnum.Plan4 },
      ],
    },
    {
      title: 'Users',
      key: 'users',
      render: (_: any, record: IOrganisation) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/organisations/${record.id}/users`)}
        >
          View Users
        </Button>
      ),
    },
    {
      title: 'Events',
      key: 'events',
      render: (_: any, record: IOrganisation) => (
        <Button
          type="link"
          onClick={() => navigate(`/admin/organisations/${record.id}/events`)}
        >
          View Events
        </Button>
      ),
    },
    {
      title: 'Features',
      key: 'features',
      render: (_: any, record: IOrganisation) => (
        <Button 
          type="link" 
          onClick={() => navigate(`/admin/organisations/${record.id}/features`)}
        >
          Manage Features
        </Button>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: IOrganisation) => (
        <Space size="small">
          <Button 
            icon={<EyeOutlined />} 
            onClick={() => handleViewOrganisation(record.id)} 
            title="View"
          />
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEditOrganisation(record.id)} 
            title="Edit"
          />
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteOrganisation(record.id, record.name)} 
            title="Delete"
          />
        </Space>
      ),
    },
  ];

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.organisationsName'} defaultMessage="Organisations" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <Card
        title={
          <Title level={2}>
            <FormattedMessage id={'admin.organisations.title'} defaultMessage="Manage Organisations" />
          </Title>
        }
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setCreateModalVisible(true)}
          >
            <FormattedMessage id={'admin.organisations.create'} defaultMessage="Create Organisation" />
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Search
            placeholder={intl.formatMessage({ id: 'admin.organisations.searchPlaceholder' }) || "Search organisations by name"}
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onSearch={value => setSearchText(value)}
            style={{ marginBottom: 16 }}
          />
          
          <Table 
            dataSource={organisations} 
            columns={columns} 
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
          />
        </Space>
      </Card>
      
      {/* Create Organisation Modal */}
      <Modal
        title={intl.formatMessage({ id: 'admin.organisations.createModalTitle' }) || "Create New Organisation"}
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleCreateOrganisation}
        >
          <Form.Item
            name="name"
            label={intl.formatMessage({ id: 'admin.organisations.nameLabel' }) || "Organisation Name"}
            rules={[{ required: true, message: 'Please enter organisation name' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            name="plan"
            label={intl.formatMessage({ id: 'admin.organisations.planLabel' }) || "Plan Level"}
            rules={[{ required: true, message: 'Please select a plan' }]}
            initialValue={PlanLevelEnum.Plan1}
          >
            <Select>
              <Select.Option value={PlanLevelEnum.Plan1}>Basic</Select.Option>
              <Select.Option value={PlanLevelEnum.Plan2}>Standard</Select.Option>
              <Select.Option value={PlanLevelEnum.Plan3}>Professional</Select.Option>
              <Select.Option value={PlanLevelEnum.Plan4}>Enterprise</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="adminEmail"
            label={intl.formatMessage({ id: 'admin.organisations.adminEmailLabel' }) || "Admin Email"}
            rules={[
              { required: true, message: 'Please enter admin email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                <FormattedMessage id={'common.create'} defaultMessage="Create" />
              </Button>
              <Button onClick={() => setCreateModalVisible(false)}>
                <FormattedMessage id={'common.cancel'} defaultMessage="Cancel" />
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default AdminOrganisationsPage;